import axios, { AxiosResponse } from 'axios';

import { getPrivateAppToken } from '@private-crm-app/utils';

const BASE_URL = `${process.env.API_URL}`;

interface RequestConfig {
  url: string;
  params?: Record<string, unknown>;
  data?: unknown;
  headers?: Record<string, string>;
  isAuthToken?: boolean;
  redactTransportLogs?: boolean;
  signal?: AbortSignal;
}

export async function get<T>({ url, params, headers, signal }: RequestConfig): Promise<T> {
  const token = getPrivateAppToken();
  console.log('Request URL:', `${BASE_URL}/${url}`);
  console.log('Token available:', !!token);
  console.log('Request headers:', {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...headers,
  });

  try {
    const response: AxiosResponse<T> = await axios.get(`${BASE_URL}/${url}`, {
      params,
      signal,
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...headers,
      },
    });

    return response.data;
  } catch (error) {
    console.error('API request failed:', error);
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      console.error('Error response data:', error.response.data);
      console.error('Error response status:', error.response.status);
      console.error('Error response headers:', error.response.headers);
    } else if (error.request) {
      // The request was made but no response was received
      console.error('No response received from server:', error.request);
    } else {
      // Something happened in setting up the request that triggered an Error
      console.error('Error setting up request:', error.message);
    }
    throw error;
  }
}

export async function post<T>({
  url,
  data,
  headers,
  isAuthToken = true,
  redactTransportLogs = false,
}: RequestConfig): Promise<T> {
  const token = getPrivateAppToken();
  console.log('[API POST] Request to:', `${BASE_URL}/${url}`);
  if (!redactTransportLogs) {
    console.log('[API POST] Request data:', data);
  }
  console.log('[API POST] Auth token available:', !!token);

  try {
    const response: AxiosResponse<T> = await axios.post(`${BASE_URL}/${url}`, data, {
      headers: {
        'Content-Type': 'application/json',
        ...(isAuthToken && token && { Authorization: `Bearer ${token}` }),
        ...headers,
      },
    });

    console.log('[API POST] Response status:', response.status);
    if (!redactTransportLogs) {
      console.log('[API POST] Response data:', response.data);
    }

    return response.data;
  } catch (error) {
    if (redactTransportLogs) {
      const status = error.response?.status;
      console.error('[API POST] Request failed:', {
        url: `${BASE_URL}/${url}`,
        status: status ?? 'unknown',
      });
    } else {
      console.error('[API POST] Request failed:', error);
      if (error.response) {
        console.error('[API POST] Error response data:', error.response.data);
        console.error('[API POST] Error response status:', error.response.status);
      } else if (error.request) {
        console.error('[API POST] No response received:', error.request);
      } else {
        console.error('[API POST] Error setting up request:', error.message);
      }
    }
    throw error;
  }
}

export async function put<T>({ url, data, headers }: RequestConfig): Promise<T> {
  const token = getPrivateAppToken();
  const response: AxiosResponse<T> = await axios.put(`${BASE_URL}/${url}`, data, {
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...headers,
    },
  });

  return response.data;
}
